import { z } from 'zod';
import { feedbackSchema, type TestingFeedback } from './feedback/schema';
import { legacyStyleProfileSchema, type LegacyStyleProfile } from './style/legacy-schema';
import { defaultPreferences, preferencesSchema, type Preferences } from './preferences';
import {
  styleProfileSchema,
  upgradeStyleProfileNarrative,
  type StyleProfile,
} from './style/schema';

export const localProfileSchema = z.object({
  version: z.literal(2),
  updatedAt: z.string(),
  styleProfile: styleProfileSchema.nullable(),
  archivedStyleProfileV1: legacyStyleProfileSchema.optional(),
  styleRegenerationRequired: z.boolean(),
  migrationNotice: z.string().nullable(),
  preferences: preferencesSchema,
  feedbackWeights: z.record(z.string(), z.number()),
  feedbackChanges: z.array(z.string()),
  savedIds: z.array(z.string()),
  testedIds: z.array(z.string()),
  feedback: z.array(feedbackSchema),
  refined: z.boolean(),
});

export interface LocalProfile {
  version: 2;
  updatedAt: string;
  styleProfile: StyleProfile | null;
  archivedStyleProfileV1?: LegacyStyleProfile;
  styleRegenerationRequired: boolean;
  migrationNotice: string | null;
  preferences: Preferences;
  feedbackWeights: Record<string, number>;
  feedbackChanges: string[];
  savedIds: string[];
  testedIds: string[];
  feedback: TestingFeedback[];
  refined: boolean;
}

export const createLocalProfile = (): LocalProfile => ({
  version: 2,
  updatedAt: new Date().toISOString(),
  styleProfile: null,
  styleRegenerationRequired: false,
  migrationNotice: null,
  preferences: { ...defaultPreferences },
  feedbackWeights: {},
  feedbackChanges: [],
  savedIds: [],
  testedIds: [],
  feedback: [],
  refined: false,
});

const legacyLocalProfileSchema = z.object({
  version: z.literal(1),
  updatedAt: z.string().optional(),
  styleProfile: z.unknown().nullable().optional(),
  preferences: preferencesSchema.optional(),
  feedbackWeights: z.record(z.string(), z.number()).optional(),
  feedbackChanges: z.array(z.string()).optional(),
  savedIds: z.array(z.string()).optional(),
  testedIds: z.array(z.string()).optional(),
  feedback: z.array(feedbackSchema).optional(),
  refined: z.boolean().optional(),
});

const recoverableV2ProfileSchema = z.object({
  version: z.literal(2),
  updatedAt: z.string(),
  styleProfile: z.unknown().nullable(),
  archivedStyleProfileV1: legacyStyleProfileSchema.optional(),
  styleRegenerationRequired: z.boolean(),
  migrationNotice: z.string().nullable(),
  preferences: preferencesSchema,
  feedbackWeights: z.record(z.string(), z.number()),
  feedbackChanges: z.array(z.string()),
  savedIds: z.array(z.string()),
  testedIds: z.array(z.string()),
  feedback: z.array(feedbackSchema),
  refined: z.boolean(),
});

export const migrateProfile = (input: unknown): LocalProfile => {
  const current = localProfileSchema.safeParse(input);
  if (current.success) return current.data;

  const recoverableV2 = recoverableV2ProfileSchema.safeParse(input);
  if (recoverableV2.success) {
    const styleProfile = recoverableV2.data.styleProfile
      ? upgradeStyleProfileNarrative(recoverableV2.data.styleProfile)
      : null;
    return localProfileSchema.parse({
      ...recoverableV2.data,
      styleProfile,
      styleRegenerationRequired:
        recoverableV2.data.styleRegenerationRequired ||
        (recoverableV2.data.styleProfile !== null && styleProfile === null),
      migrationNotice:
        recoverableV2.data.styleProfile !== null && styleProfile === null
          ? 'Your earlier Style Card could not be upgraded safely. Your preferences and fragrance history were preserved.'
          : recoverableV2.data.migrationNotice,
    });
  }

  const legacyCandidate =
    input && typeof input === 'object' && !('version' in input)
      ? { ...(input as object), version: 1 as const }
      : input;
  const legacy = legacyLocalProfileSchema.safeParse(legacyCandidate);
  if (!legacy.success) return createLocalProfile();

  const legacyStyle = legacyStyleProfileSchema.safeParse(legacy.data.styleProfile);
  const alreadyControlled = styleProfileSchema.safeParse(legacy.data.styleProfile);
  return localProfileSchema.parse({
    ...createLocalProfile(),
    updatedAt: legacy.data.updatedAt ?? new Date().toISOString(),
    styleProfile: alreadyControlled.success ? alreadyControlled.data : null,
    archivedStyleProfileV1: legacyStyle.success ? legacyStyle.data : undefined,
    styleRegenerationRequired: legacyStyle.success,
    migrationNotice: legacyStyle.success
      ? 'STYLE now uses a controlled visual vocabulary. Your preferences, saved fragrances and testing history were preserved, but your visual Style Card needs to be regenerated.'
      : null,
    preferences: legacy.data.preferences ?? { ...defaultPreferences },
    feedbackWeights: legacy.data.feedbackWeights ?? {},
    feedbackChanges: legacy.data.feedbackChanges ?? [],
    savedIds: legacy.data.savedIds ?? [],
    testedIds: legacy.data.testedIds ?? [],
    feedback: legacy.data.feedback ?? [],
    refined: legacy.data.refined ?? false,
  });
};
